// Traditional Redux implementation of stock chart actions
import {getStockData,getSimpleStockRangeBatch} from './stockDataAPI'
import { createAsyncThunk } from '@reduxjs/toolkit';

// Action types
export const FETCH_STOCK_DATA_REQUEST = 'FETCH_STOCK_DATA_REQUEST';
export const FETCH_STOCK_DATA_SUCCESS = 'FETCH_STOCK_DATA_SUCCESS';
export const FETCH_STOCK_DATA_FAILURE = 'FETCH_STOCK_DATA_FAILURE';
export const SET_STOCK_SYMBOL = 'SET_STOCK_SYMBOL';
export const SET_CHART_TIMEFRAME = 'SET_CHART_TIMEFRAME';
export const SET_SELECTED_INDICATORS = 'SET_SELECTED_INDICATORS';
export const ADD_CHART_INDICATOR = 'ADD_CHART_INDICATOR';
export const REMOVE_CHART_INDICATOR = 'REMOVE_CHART_INDICATOR';
export const SET_CHART_DATE_RANGE = 'SET_CHART_DATE_RANGE';
export const RESET_CHART_DATE_RANGE = 'RESET_CHART_DATE_RANGE';
export const CLEAR_CHART_ERROR = 'CLEAR_CHART_ERROR';

// Time frame mapping
const timeframeMap = {
  '1D': 'daily',
  '1W': 'weekly',
  '1M': 'monthly',
  '1Y': 'yearly'
};

/**
 * Action creator to set the current stock symbol
 * @param {string} symbol - Stock symbol/ticker
 */
export const setStockSymbol = (symbol) => ({
  type: SET_STOCK_SYMBOL,
  payload: symbol
});

/**
 * Action creator to set the chart timeframe
 * @param {string} timeframe - Chart timeframe code ('1D', '1W', '1M', '1Y')
 */
export const setChartTimeframe = (timeframe) => ({
  type: SET_CHART_TIMEFRAME,
  payload: timeframe
});

/**
 * Action creator to set selected technical indicators
 * @param {Array<string>} indicators - Array of indicator codes
 */
export const setSelectedIndicators = (indicators) => ({
  type: SET_SELECTED_INDICATORS,
  payload: indicators
});

/**
 * Action creator to add a single indicator to the chart
 * @param {string} indicator - Indicator code to add
 */
export const addChartIndicator = (indicator) => ({
  type: ADD_CHART_INDICATOR,
  payload: indicator
});

/**
 * Action creator to remove a single indicator from the chart
 * @param {string} indicator - Indicator code to remove
 */
export const removeChartIndicator = (indicator) => ({
  type: REMOVE_CHART_INDICATOR,
  payload: indicator
});

/**
 * Action creator to set custom date range for the chart
 * @param {Object} dateRange - Date range object with startDate and endDate
 */
export const setChartDateRange = (dateRange) => ({
  type: SET_CHART_DATE_RANGE,
  payload: dateRange
});

/**
 * Action creator to reset to default date range
 */
export const resetChartDateRange = () => ({
  type: RESET_CHART_DATE_RANGE
});

/**
 * Action creator to clear chart error state
 */
export const clearChartError = () => ({
  type: CLEAR_CHART_ERROR
});

/**
 * Redux thunk to fetch chart data for a given stock
 * @param {string} symbol - Stock ticker symbol
 * @param {string} timeframe - Chart timeframe code ('1D', '1W', '1M', '1Y') 
 * @param {string|null} startDate - Custom start date (ISO format)
 * @param {string|null} endDate - Custom end date (ISO format)
 * @param {Array<string>} indicators - List of technical indicators to include
 */
export const fetchStockChartData = (
  symbol, 
  timeframe = '1W', 
  startDate = null, 
  endDate = null, 
  indicators = []
) => async (dispatch) => {
  // Convert UI timeframe codes to API format
  const apiTimeframe = timeframeMap[timeframe] || timeframe;
  
  dispatch({ type: FETCH_STOCK_DATA_REQUEST });

  try {
    const response = await getStockData(
      symbol, 
      apiTimeframe, 
      startDate, 
      endDate, 
      indicators
    );
    
    dispatch({
      type: FETCH_STOCK_DATA_SUCCESS,
      payload: {
        chartData: response.data,
        indicators: response.indicators,
        symbol,
        timeframe
      }
    });
    
    return response;
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch stock chart data';
    
    dispatch({
      type: FETCH_STOCK_DATA_FAILURE,
      payload: errorMessage
    });
    
    throw error; // Allow the calling code to also handle the error
  }
};

/**
 * Thunk to automatically refresh chart data when parameters change
 */
export const refreshStockChartData = () => (dispatch, getState) => {
  const { 
    symbol, 
    timeframe, 
    selectedIndicators,
    dateRange 
  } = getState().stockChart;
  
  if (!symbol) return; // Don't fetch without a symbol
  
  return dispatch(fetchStockChartData(
    symbol,
    timeframe,
    dateRange.isCustomRange ? dateRange.startDate : null,
    dateRange.isCustomRange ? dateRange.endDate : null,
    selectedIndicators
  ));
};

// Async Thunk to fetch stock data
export const fetchStockDataThunk = createAsyncThunk(
  'stock/fetchData',
  async ({ symbol, timeFrame, indicators }, { rejectWithValue }) => {
    try {
      // Normalize here:
      const timeframeMap = {
        '1D': 'daily',
        '1W': 'weekly',
        '1M': 'monthly',
        '1Y': 'yearly'
      };
      const apiTimeframe = timeframeMap[timeFrame] || timeFrame;

      const data = await getStockData(symbol, apiTimeframe, undefined, undefined, indicators);
      return data;
    } catch (error) {
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);


// Fetch lightweight 90D charts for watchlist symbols
export const fetchWatchlistSimpleChartsThunk = createAsyncThunk(
  "stock/fetchWatchlistSimpleCharts",
  async ({ symbols = [], days = 90 }, { getState, rejectWithValue }) => {
    try {
      const state = getState().stockData;

      const cache = state.watchlistSimpleCharts || {};
      const now = Date.now();
      const ttlMs = 120 * 1000;

      const normalized = symbols
        .filter(Boolean)
        .map((s) => String(s).toUpperCase());

      // Determine which symbols must be fetched
      const toFetch = normalized.filter((sym) => {
        const entry = cache[sym];

        if (!entry?.fetchedAt) return true;
        return now - entry.fetchedAt > ttlMs;
      });


      if (!toFetch.length) {
        //console.log("✅ All data fresh — returning empty {}");
        //console.log("===============================================");
        return {};
      }

      // One batched request for every symbol that needs refreshing, instead
      // of a 5-worker queue firing one individual request per symbol (was up
      // to ~50-60 on the Dashboard/StockOverviewPage watchlist). See
      // Technical_Docs/13_FIX_HISTORY.md's seventy-second pass.
      const batchResult = await getSimpleStockRangeBatch(toFetch, days);

      const results = toFetch.map((sym) => {
        const payload = batchResult[sym];
        if (payload) {
          return [sym, { ...payload, fetchedAt: Date.now(), days }];
        }
        return [
          sym,
          {
            data: [],
            indicators: {},
            fetchedAt: Date.now(),
            days,
            error: "Failed",
          },
        ];
      });

      const finalObject = Object.fromEntries(results);

      return finalObject;
    } catch (error) {

      return rejectWithValue(
        error?.response ? error.response.data : error.message
      );
    }
  }
);
